import { cn } from '@rocco/utils/cn';
import { ReactComponent as ApMark } from '@rocco/icons/svg/archipro-receipt-mark.svg';
import {
    gatePaneImageAttrs,
    isGatePaneImageMissing,
    type GatePaneImage,
} from '~/modules-rocco/user/auth-gate/gate-pane-image';

interface AuthModalBrandPaneProps {
    /** Statement overlaid on the imagery (from the intent registry). */
    statement: string;
    /**
     * What to show behind the statement. Required, and the two sentinels are
     * distinct on purpose: a surface with no item behind it passes
     * `GATE_PANE_BRAND_DEFAULT`, while an intent gate resolves its item's
     * imagery through `resolveGatePaneImage`. Silently omitting it is what let
     * item gates ship with the stock photo.
     */
    image: GatePaneImage;
    /**
     * Secondary line under the statement, smaller. The professional's own
     * enquiry message goes here rather than above the form, where it pushed
     * the first field below the fold.
     */
    note?: string;
    className?: string;
}

/**
 * Default left pane of the auth modal: brand imagery + white AP mark +
 * value-prop statement. Intent surfaces that need bespoke left-pane content
 * (e.g. the digital publications subscription checklist) pass their own
 * component via AuthModal's `brandPane` prop instead.
 */
export const AuthModalBrandPane = (props: AuthModalBrandPaneProps) => {
    const { statement, image, note, className } = props;
    const showMissingRibbon =
        import.meta.env.DEV && isGatePaneImageMissing(image);

    return (
        <div className={cn('relative size-full', className)}>
            <img
                {...gatePaneImageAttrs(image)}
                alt=""
                className="absolute inset-0 size-full object-cover"
            />
            <div className="absolute inset-0 bg-black/20" />
            <ApMark className="absolute top-10 left-7 h-auto w-28 [&_path]:fill-white" />
            {showMissingRibbon ? (
                <div className="absolute top-0 right-0 left-0 bg-red-600 px-3 py-1.5 text-center text-xs font-medium text-white">
                    Dev only: no item imagery, showing stock. See console.
                </div>
            ) : null}
            <div className="absolute bottom-12.5 left-7 w-95.5">
                <p className="m-0 text-[1.75rem] leading-[0.975] font-medium tracking-[-0.03em] text-white">
                    {statement}
                </p>
                {note ? (
                    <p className="m-0 mt-3 text-[0.8125rem] leading-snug whitespace-pre-wrap text-white/80">
                        {note}
                    </p>
                ) : null}
            </div>
        </div>
    );
};
